import { CheckCircle2 } from "lucide-react";

const DEFAULT_TIME_ZONE = "Europe/Rome";

const parseDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value, timeZone) => {
  const date = parseDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat("it-IT", {
    timeZone,
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const formatTime = (value, timeZone) => {
  const date = parseDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("it-IT", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatTimeRange = (startTime, endTime, timeZone) => {
  const start = formatTime(startTime, timeZone);
  const end = formatTime(endTime, timeZone);
  if (start && end) return `${start} – ${end}`;
  return start || end || "-";
};

const formatDuration = (startTime, endTime) => {
  const start = Date.parse(startTime);
  const end = Date.parse(endTime);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return "-";
  const minutes = (end - start) / 60000;
  if (!Number.isInteger(minutes)) return "-";
  return `${minutes} ${minutes === 1 ? "minuto" : "minuti"}`;
};

export const getOwnerAppointmentStatusLabel = (appointment, now = new Date()) => {
  const status = appointment?.status;
  if (status === "completed") return "Svolto";
  if (status === "cancelled") return "Annullato";
  if (status === "no_show") return "No-show";
  if (status === "scheduled") {
    const endTimestamp = Date.parse(appointment?.end_time);
    const nowTimestamp = now instanceof Date ? now.getTime() : Date.parse(now);
    return Number.isFinite(endTimestamp) && Number.isFinite(nowTimestamp) && endTimestamp <= nowTimestamp
      ? "Da chiudere"
      : "Programmato";
  }
  if (status === "requested") return "Richiesto";
  if (status === "recovered") return "Recuperato";
  return status || "-";
};

function SummaryItem({ label, value, testId }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-emerald-800">{label}</dt>
      <dd className="mt-1 break-words text-base font-semibold text-slate-950" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

export function OwnerAppointmentSuccess({
  successSummary,
  onBackToAgenda,
  onCreateAnotherAppointment,
  businessTimezone = DEFAULT_TIME_ZONE,
  now = new Date(),
}) {
  const appointment = successSummary?.appointment || {};
  const startTime = appointment.start_time;
  const endTime = appointment.end_time;

  return (
    <div
      className="mobile-safe-bottom min-w-0"
      data-testid="owner-appointment-success-screen"
    >
      <section
        className="rounded-sm border border-emerald-200 bg-emerald-50 p-4 sm:p-6"
        aria-labelledby="owner-appointment-success-title"
        data-testid="owner-appointment-success-container"
      >
        <div className="flex min-w-0 items-start gap-3" role="status" aria-live="polite" aria-atomic="true">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-white"
            aria-hidden="true"
          >
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <h1
              id="owner-appointment-success-title"
              tabIndex={-1}
              data-spa-page-title="true"
              className="break-words text-2xl font-bold tracking-tight text-emerald-950"
            >
              Appuntamento creato
            </h1>
            <p className="mt-1 text-sm leading-6 text-emerald-900">
              L&apos;appuntamento è stato salvato con successo.
            </p>
          </div>
        </div>

        <div
          className="mt-5 rounded-sm border border-emerald-100 bg-white p-4 shadow-sm sm:p-5"
          data-testid="owner-appointment-success-summary"
        >
          <h2 className="text-base font-bold text-slate-950">Riepilogo appuntamento</h2>
          <dl className="mt-4 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
            <SummaryItem
              label="Cliente"
              value={successSummary?.customerName || "-"}
              testId="owner-appointment-success-customer"
            />
            <SummaryItem
              label="Servizio"
              value={successSummary?.serviceName || "-"}
              testId="owner-appointment-success-service"
            />
            <SummaryItem
              label="Operatrice"
              value={successSummary?.staffName || "Non assegnata"}
              testId="owner-appointment-success-staff"
            />
            <SummaryItem
              label="Data"
              value={formatDate(startTime, businessTimezone)}
              testId="owner-appointment-success-date"
            />
            <SummaryItem
              label="Orario"
              value={formatTimeRange(startTime, endTime, businessTimezone)}
              testId="owner-appointment-success-time"
            />
            <SummaryItem
              label="Durata"
              value={formatDuration(startTime, endTime)}
              testId="owner-appointment-success-duration"
            />
            <SummaryItem
              label="Stato"
              value={getOwnerAppointmentStatusLabel(appointment, now)}
              testId="owner-appointment-success-status"
            />
          </dl>
        </div>
      </section>

      <div className="mt-6" data-testid="owner-appointment-success-actions">
        <button
          type="button"
          onClick={onBackToAgenda}
          className="inline-flex min-h-14 w-full items-center justify-center rounded-md bg-blue-700 px-5 py-3 text-base font-bold text-white shadow-sm"
          data-testid="owner-appointment-success-agenda"
        >
          Torna all&apos;Agenda
        </button>
        <p className="my-4 text-center text-sm font-medium text-slate-500">oppure</p>
        <button
          type="button"
          onClick={onCreateAnotherAppointment}
          className="min-h-12 w-full rounded-lg border border-emerald-300 bg-white px-4 py-3 text-sm font-semibold text-emerald-800"
          data-testid="owner-appointment-success-another"
        >
          Crea un altro appuntamento
        </button>
      </div>
    </div>
  );
}
